import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  BoQSection,
  BoQSummary,
  BoQExportOptions,
  TenderState,
} from "../types/tender.types";
import { ExportDetailOptions } from "../components/common/ExportOptionsModal";
import mjLogoPath from "../assets/mnj-logo.jpg";
import { 
  exportBillSummaryWithLogo, 
  ExcelJSColumn,
  exportWithLogoFallback 
} from "./excelJsUtils";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// jspdf-autotable v5 no longer self-registers on the jsPDF prototype in a bundled
// (non-<script>-tag) environment, so doc.autoTable()/doc.lastAutoTable must be
// enabled explicitly once here.
applyPlugin(jsPDF);

export class BoQExporter {
  private static exportDetailOptions: ExportDetailOptions | null = null;

  /**
   * Set export detail options for filtering data
   */
  static selectExportDetails(options: ExportDetailOptions): void {
    this.exportDetailOptions = options;
  }

  /**
   * Build dynamic column styles for PDF based on export options
   */
  private static buildPDFColumnStyles(): any {
    const styles: any = {};
    let colIndex = 0;
    
    // Item column (always present)
    styles[colIndex] = { cellWidth: 15 };
    colIndex++;
    
    // Description column
    if (this.exportDetailOptions?.includeDescriptions || !this.exportDetailOptions) {
      styles[colIndex] = { cellWidth: 60, cellPadding: 3, valign: "top", lineWidth: 0.1 };
      colIndex++;
    }
    
    // Quantity and Unit columns
    if (this.exportDetailOptions?.includeQuantities || !this.exportDetailOptions) {
      styles[colIndex] = { cellWidth: 15, halign: "right", valign: "middle" }; // Qty
      colIndex++;
      styles[colIndex] = { cellWidth: 15, halign: "center", valign: "middle" }; // Unit
      colIndex++;
    }
    
    // Rate column
    if (this.exportDetailOptions?.includeRates || !this.exportDetailOptions) {
      styles[colIndex] = { cellWidth: 25, halign: "right", valign: "middle" };
      colIndex++;
    }
    
    // Amount column
    if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
      styles[colIndex] = { cellWidth: 30, halign: "right", valign: "middle" };
      colIndex++;
    }
    
    return styles;
  }

  /**
   * Filter export data based on selected detail options
   */
  private static filterExportData(sections: BoQSection[]): BoQSection[] {
    if (!this.exportDetailOptions) {
      return sections; // Return original data if no options set
    }

    const options = this.exportDetailOptions;
    
    return sections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Always include headers and structural elements
        if (item.isHeader || item.isTotal || item.isSubtotal) {
          return true;
        }

        // Filter based on options
        if (!options.includeDescriptions && !item.description) {
          return false;
        }

        // If quantities are disabled, only show items without quantities or with zero quantities
        if (!options.includeQuantities && item.quantity && item.quantity > 0) {
          return options.includeDescriptions; // Keep if descriptions are enabled
        }

        // If rates are disabled, only show items without rates
        if (!options.includeRates && item.rate && item.rate > 0) {
          return options.includeDescriptions || options.includeQuantities;
        }

        return true;
      }).map(item => ({
        ...item,
        // Filter item properties based on options
        description: options.includeDescriptions ? item.description : '',
        quantity: options.includeQuantities ? item.quantity : undefined,
        rate: options.includeRates ? item.rate : undefined,
        amount: options.includeTotals ? item.amount : 0,
        notes: options.includeNotes ? item.notes : undefined
      }))
    })).filter(section => 
      // Remove empty sections if groupByCategory is disabled
      options.groupByCategory || section.items.length > 0
    );
  }
  /**
   * Add M&J logo and branding to PDF
   */
  private static async addLogoToPDF(doc: jsPDF, y: number = 15): Promise<number> {
    try {
      // Convert logo to base64 if needed for PDF
      const logoBase64 = await this.getLogoAsBase64();
      if (logoBase64) {
        // Add logo (left side)
        doc.addImage(logoBase64, 'JPEG', 15, y, 40, 15);
        
        // Company text (right side)
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("M & J EVANS CONSTRUCTION LIMITED", 200, y + 8, { align: "right" });
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text("The Groundworks Partner of Choice", 200, y + 15, { align: "right" });
        
        return y + 25; // Return new Y position after logo
      }
    } catch (error) {
      console.warn("Could not load logo for PDF:", error);
    }
    
    // Fallback to text-only branding
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("M & J EVANS CONSTRUCTION LIMITED", 105, y + 10, { align: "center" });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("The Groundworks Partner of Choice", 105, y + 20, { align: "center" });
    
    return y + 30;
  }

  /**
   * Convert logo to base64 for PDF embedding
   */
  private static getLogoAsBase64(): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = img.width;
          canvas.height = img.height;
          
          ctx?.drawImage(img, 0, 0);
          
          const base64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(base64);
        };
        
        img.onerror = () => {
          console.warn("Could not load logo image");
          resolve(null);
        };
        
        img.src = mjLogoPath;
      } catch (error) {
        console.warn("Error converting logo to base64:", error);
        resolve(null);
      }
    });
  }
  /**
   * Enhanced Excel export with embedded logo using ExcelJS
   */
  static async exportToExcelWithLogo(
    sections: BoQSection[],
    _summary: BoQSummary,
    tenderState: TenderState,
    options: BoQExportOptions = {
      includeBreakdown: true,
      includeNotes: false,
      showRatesTo: 2,
      groupByCategory: true,
      companyHeader: true,
    }
  ): Promise<void> {
    // Apply export detail filtering
    const filteredSections = this.filterExportData(sections);

    // Transform BoQ sections to flat data structure for ExcelJS
    const exportData: any[] = [];
    
    filteredSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.description === "") {
          // Skip empty rows
          return;
        }
        
        if (item.isHeader && item.level === 0) {
          // Add section header
          exportData.push({
            description: item.description.toUpperCase(),
            quantity: "",
            unit: "",
            rate: "",
            ohp: "",
            amount: "",
            isHeader: true
          });
        } else if (!item.isHeader) {
          // Regular item
          const rowData: any = {
            description: item.description,
            isHeader: false
          };

          if (this.exportDetailOptions?.includeQuantities || !this.exportDetailOptions) {
            rowData.quantity = item.quantity || "";
            rowData.unit = item.unit || "";
          }

          if (this.exportDetailOptions?.includeRates || !this.exportDetailOptions) {
            rowData.rate = item.rate ? `£${item.rate.toFixed(options.showRatesTo)}` : "";
          }

          if (this.exportDetailOptions?.includeOhp || !this.exportDetailOptions) {
            rowData.ohp = item.ohp ? `${item.ohp}%` : "";
          }

          if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
            rowData.amount = item.total ? `£${item.total.toFixed(2)}` : "";
          }

          exportData.push(rowData);

          // Add status if present and notes enabled
          if (item.status && (this.exportDetailOptions?.includeNotes || !this.exportDetailOptions)) {
            exportData.push({
              description: `  ${item.status}`,
              quantity: "",
              unit: "",
              rate: "",
              ohp: "",
              amount: "",
              isHeader: false,
              isNote: true
            });
          }
        }
      });
    });

    // Prepare columns for ExcelJS with optimized widths for single page printing
    const columns: ExcelJSColumn[] = [
      { key: "description", title: "Description", width: 48 } // Wide for excellent readability
    ];

    if (this.exportDetailOptions?.includeQuantities || !this.exportDetailOptions) {
      columns.push({ key: "quantity", title: "Qty", width: 8 }); // Increased for visibility
      columns.push({ key: "unit", title: "Unit", width: 7 }); // Increased for visibility
    }

    if (this.exportDetailOptions?.includeRates || !this.exportDetailOptions) {
      columns.push({ key: "rate", title: "Rate", width: 11 }); // Increased for visibility
    }

    if (this.exportDetailOptions?.includeOhp || !this.exportDetailOptions) {
      columns.push({ key: "ohp", title: "OH&P", width: 8 }); // Increased for visibility
    }

    if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
      columns.push({ key: "amount", title: "Amount", width: 14 }); // Increased for visibility
    }

    // Use ExcelJS export with logo
    const filename = `BoQ_Export_${new Date().toISOString().split('T')[0]}`;
    const projectName = tenderState.projectInfo?.siteName || 
                       tenderState.projectInfo?.clientName || 
                       "Project Name Not Specified";

    try {
      await exportBillSummaryWithLogo({
        filename,
        sheetName: "Bill of Quantities",
        columns,
        data: exportData,
        includeLogo: true,
        projectName,
        reportTitle: "GENERAL SUMMARY - BILL OF QUANTITIES",
        includeEndCover: true,
        clientName: tenderState.projectInfo?.clientName || tenderState.client,
        siteAddress: tenderState.projectInfo?.siteAddress || tenderState.projectInfo?.siteName,
        dateCreated: new Date(),
        jobType: "Roads & Sewers"
      });
    } catch (error) {
      console.warn('ExcelJS export failed, falling back to XLSX:', error);

      // Fallback to original XLSX export
      this.exportToExcel(sections, _summary, tenderState, options);
    }
  }

  /**
   * Export to Excel with exact formatting to match screenshots (Original XLSX method)
   */
  static exportToExcel(
    sections: BoQSection[],
    _summary: BoQSummary,
    tenderState: TenderState,
    options: BoQExportOptions = {
      includeBreakdown: true,
      includeNotes: false,
      showRatesTo: 2,
      groupByCategory: true,
      companyHeader: true,
    }
  ): void {
    // Apply export detail filtering
    const filteredSections = this.filterExportData(sections);
    
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];

    // Company Header with enhanced branding
    if (options.companyHeader) {
      wsData.push(["M & J EVANS CONSTRUCTION LIMITED", "", "", "LOGO: See M&J Company Logo"]);
      wsData.push(["The Groundworks Partner of Choice"]);
      wsData.push(["ENTER SITE ADDRESS"]);
      wsData.push(["GENERAL SUMMARY"]);
      wsData.push([`Report Generated: ${new Date().toLocaleDateString()}`]);
      wsData.push([`Logo File: src/assets/mnj-logo.jpg`]);
      wsData.push([]);
    }

    // Dynamic headers based on export options
    const headers = ["Description"];
    if (this.exportDetailOptions?.includeQuantities) headers.push("Qty", "Unit");
    if (this.exportDetailOptions?.includeRates) headers.push("Rate");
    if (this.exportDetailOptions?.includeOhp) headers.push("OH&P");
    if (this.exportDetailOptions?.includeTotals) headers.push("Amount");
    
    // Fallback to simple format if no options set
    if (!this.exportDetailOptions) {
      headers.push("Amount");
    }
    
    wsData.push(headers);

    // Process each section to match screenshot format
    filteredSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.description === "") {
          // Empty row for spacing
          wsData.push(["", ""]);
        } else if (item.isHeader && item.level === 0) {
          // Main section header (ROADS & SEWERS)
          wsData.push([item.description.toUpperCase(), ""]);
        } else {
          // Regular items - build row based on selected options
          const row: any[] = [item.description];
          
          if (this.exportDetailOptions?.includeQuantities) {
            row.push(item.quantity || "", item.unit || "");
          }
          if (this.exportDetailOptions?.includeRates) {
            row.push(item.rate ? item.rate.toFixed(2) : "");
          }
          if (this.exportDetailOptions?.includeOhp) {
            const ohp = item.amount && item.rate && item.quantity ? 
              (item.amount - (item.rate * item.quantity)) : 0;
            row.push(ohp > 0 ? ohp.toFixed(2) : "");
          }
          if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
            row.push(item.amount > 0 ? item.amount.toFixed(2) : "");
          }
          
          wsData.push(row);

          // Add status text if present and notes are enabled
          if (item.status && this.exportDetailOptions?.includeNotes) {
            const statusRow = new Array(headers.length).fill("");
            statusRow[1] = item.status;
            wsData.push(statusRow);
          }
        }
      });
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Apply styling to match screenshot
    this.applyScreenshotStyling(ws, wsData, filteredSections);

    // Set column widths based on included columns - optimized for single page printing with wrapping
    const colWidths: any[] = [{ width: 60, wrapText: true }]; // Description - much wider for excellent readability
    if (this.exportDetailOptions?.includeQuantities) {
      colWidths.push({ width: 5 }, { width: 5 }); // Qty, Unit - ultra compact
    }
    if (this.exportDetailOptions?.includeRates) {
      colWidths.push({ width: 7 }); // Rate - compact for currency
    }
    if (this.exportDetailOptions?.includeOhp) {
      colWidths.push({ width: 5 }); // OH&P - ultra compact for percentage
    }
    if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
      colWidths.push({ width: 10 }); // Amount - compact for currency
    }

    ws["!cols"] = colWidths;

    // Set row heights for better readability (apply to all data rows)
    if (!ws["!rows"]) ws["!rows"] = [];
    for (let i = 0; i < wsData.length; i++) {
      // Calculate height based on description cell content (first column)
      const rowData = wsData[i];
      let rowHeight = 26; // Minimum height

      if (rowData && rowData[0] && typeof rowData[0] === 'string') {
        const lineCount = (rowData[0].match(/\n/g) || []).length + 1;
        const charsPerLine = 60; // Column width is 60 units
        const estimatedWrappedLines = Math.ceil(rowData[0].length / charsPerLine);
        const totalLines = Math.max(lineCount, estimatedWrappedLines);
        const extraPadding = 6;
        rowHeight = Math.max(26, (totalLines * 16) + extraPadding);
      }

      ws["!rows"][i] = { hpt: rowHeight, hpx: rowHeight };
    }

    // Set page setup for printing - force fit to one page width
    ws["!pageSetup"] = {
      paperSize: 9, // A4
      orientation: 'portrait',
      fitToWidth: 1, // Force all columns on one page width
      fitToHeight: 0, // Allow multiple pages vertically
      scale: 95 // Slightly reduce scale for better fit
    };

    // Set smaller margins for XLSX
    ws["!margins"] = {
      left: 0.3,
      right: 0.3,
      top: 0.4,
      bottom: 0.4,
      header: 0.15,
      footer: 0.15
    };

    // Enable text wrapping for all cells with proper alignment
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[cellAddress]) {
          if (!ws[cellAddress].s) ws[cellAddress].s = {};

          // Determine horizontal alignment
          let horizontalAlign = 'center';
          if (C === 0) {
            horizontalAlign = 'left'; // Description column
          } else if (C > 1) {
            horizontalAlign = 'right'; // Numbers, currency columns
          }

          ws[cellAddress].s.alignment = {
            wrapText: true,
            vertical: 'top',
            horizontal: horizontalAlign,
            shrinkToFit: false
          };
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "General Summary");

    // Generate filename and save
    const filename = `${tenderState.projectName || "Project"}_BoQ_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  /**
   * Apply Excel styling to match screenshot format exactly
   */
  private static applyScreenshotStyling(
    ws: XLSX.WorkSheet,
    _wsData: any[][],
    sections: BoQSection[]
  ): void {
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1:B1");

    // Track which rows need yellow highlighting
    const yellowRows: Set<number> = new Set();
    let rowIndex = 0;

    // Skip header rows (now 6 due to added tagline)
    rowIndex = 6; // After company header and table header

    // Identify rows that need yellow highlighting based on BoQ items
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.description === "") {
          rowIndex++;
        } else if (item.isHeader && item.level === 0) {
          rowIndex++;
        } else {
          if (item.isHighlighted || item.isSavingsItem) {
            yellowRows.add(rowIndex);
          }
          rowIndex++;

          // If status text, increment row count
          if (item.status) {
            rowIndex++;
          }
        }
      });
    });

    // Apply cell formatting
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellRef];

        if (cell) {
          // Initialize cell style if it doesn't exist
          if (!cell.s) cell.s = {};

          // Yellow highlighting for specific rows
          if (yellowRows.has(row)) {
            cell.s.fill = {
              fgColor: { rgb: "FFFF00" }, // Yellow background
            };
          }

          // Header row styling (row 5 - Description/Amount, adjusted for new tagline)
          if (row === 5) {
            cell.s.fill = {
              fgColor: { rgb: "FFFF00" }, // Yellow background for headers
            };
            cell.s.font = {
              bold: true,
            };
          }

          // Company header styling
          if (row === 0) {
            cell.s.font = {
              bold: true,
              size: 14,
              color: { rgb: "1E3A8A" }, // Blue color for company name
            };
          }

          // Tagline styling
          if (row === 1) {
            cell.s.font = {
              italic: true,
              size: 10,
              color: { rgb: "64748B" }, // Gray color for tagline
            };
          }

          // "ENTER SITE ADDRESS" styling
          if (row === 2) {
            cell.s.fill = {
              fgColor: { rgb: "FFFF00" }, // Yellow background
            };
          }

          // Number formatting for amounts
          if (col === 1 && cell.v && typeof cell.v === "number") {
            cell.z = "#,##0.00";
          }

          // Add borders
          cell.s.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }
      }
    }

    // Set print settings
    ws["!margins"] = {
      left: 0.7,
      right: 0.7,
      top: 0.75,
      bottom: 0.75,
      header: 0.3,
      footer: 0.3,
    };
  }

  /**
   * Export to PDF with professional formatting and M&J branding
   */
  static async exportToPDF(
    sections: BoQSection[],
    summary: BoQSummary,
    tenderState: TenderState,
    options: BoQExportOptions = {
      includeBreakdown: true,
      includeNotes: false,
      showRatesTo: 2,
      groupByCategory: true,
      companyHeader: true,
    }
  ): Promise<void> {
    // Apply export detail filtering
    const filteredSections = this.filterExportData(sections);
    
    const doc = new jsPDF();

    // Add M&J Logo and Branding
    let yPosition = 15;
    if (options.companyHeader) {
      yPosition = await this.addLogoToPDF(doc, yPosition);
      
      // Add document title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("BILL OF QUANTITIES", 105, yPosition + 10, { align: "center" });

      // Add project information
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Project: ${tenderState.projectName}`, 20, yPosition + 25);
      doc.text(`Client: ${tenderState.client}`, 20, yPosition + 35);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, yPosition + 45);
      
      yPosition += 60;
    } else {
      yPosition = 30;
    }

    // Process each section
    filteredSections.forEach((section, _sectionIndex) => {
      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 30;
      }

      // Prepare table data for this section
      const tableData: any[][] = [];

      section.items.forEach((item) => {
        if (item.isHeader && item.level === 0) {
          // Main section header - add as separate heading
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(item.description, 20, yPosition);
          yPosition += 10;
        } else if (item.isHeader && item.level === 1) {
          // Subsection header
          tableData.push([
            { content: item.itemNumber, styles: { fontStyle: "bold" } },
            { content: item.description, styles: { fontStyle: "bold" } },
            "",
            "",
            "",
            "",
          ]);
        } else if (item.level === 2 && !item.isSubtotal && !item.isTotal) {
          // Regular line item - build based on export options
          const row = [item.itemNumber || ""];
          
          if (this.exportDetailOptions?.includeDescriptions || !this.exportDetailOptions) {
            row.push(item.description || "");
          }
          if (this.exportDetailOptions?.includeQuantities || !this.exportDetailOptions) {
            row.push(item.quantity ? item.quantity.toFixed(2) : "");
            row.push(item.unit || "");
          }
          if (this.exportDetailOptions?.includeRates || !this.exportDetailOptions) {
            row.push(item.rate ? `£${item.rate.toFixed(options.showRatesTo || 2)}` : "");
          }
          if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
            row.push(item.amount ? `£${item.amount.toFixed(2)}` : "");
          }
          
          tableData.push(row);
        } else if (item.isSubtotal) {
          // Subtotal line
          tableData.push([
            "",
            { content: item.description, styles: { fontStyle: "italic" } },
            "",
            "",
            "",
            {
              content: `£${item.amount.toFixed(2)}`,
              styles: { fontStyle: "bold" },
            },
          ]);
        } else if (item.isTotal) {
          // Section total
          tableData.push([
            "",
            {
              content: item.description,
              styles: { fontStyle: "bold", fontSize: 12 },
            },
            "",
            "",
            "",
            {
              content: `£${item.amount.toFixed(2)}`,
              styles: { fontStyle: "bold", fontSize: 12 },
            },
          ]);
        } else if (item.description === "") {
          // Empty row for spacing
          tableData.push(["", "", "", "", "", ""]);
        }
      });

      // Add table for this section
      if (tableData.length > 0) {
        // Build dynamic headers for PDF based on export options
        const pdfHeaders = ["Item"];
        if (this.exportDetailOptions?.includeDescriptions || !this.exportDetailOptions) {
          pdfHeaders.push("Description");
        }
        if (this.exportDetailOptions?.includeQuantities || !this.exportDetailOptions) {
          pdfHeaders.push("Qty", "Unit");
        }
        if (this.exportDetailOptions?.includeRates || !this.exportDetailOptions) {
          pdfHeaders.push("Rate");
        }
        if (this.exportDetailOptions?.includeTotals || !this.exportDetailOptions) {
          pdfHeaders.push("Amount");
        }
        
        doc.autoTable({
          startY: yPosition,
          head: [pdfHeaders],
          body: tableData,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            cellWidth: 'wrap',
            overflow: 'linebreak',
            valign: 'top',
          },
          headStyles: {
            fillColor: [200, 200, 200],
            fontStyle: "bold",
            fontSize: 10,
          },
          columnStyles: this.buildPDFColumnStyles(),
          margin: { left: 20, right: 20 },
          pageBreak: "avoid",
        });

        yPosition = (doc as any).lastAutoTable.finalY + 10;
      }
    });

    // Grand Total
    if (yPosition > 260) {
      doc.addPage();
      yPosition = 30;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`GRAND TOTAL: £${summary.grandTotal.toFixed(2)}`, 105, yPosition, {
      align: "center",
    });

    // Add breakdown page if requested and enabled in export options
    if (options.includeBreakdown && (this.exportDetailOptions?.includeBreakdown !== false)) {
      await this.addBreakdownPage(doc, summary);
    }

    // Save PDF
    const filename = `${tenderState.projectName || "Project"}_BoQ_${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    doc.save(filename);
  }

  /**
   * Add cost breakdown page to PDF with branding
   */
  private static async addBreakdownPage(doc: jsPDF, summary: BoQSummary): Promise<void> {
    doc.addPage();

    // Add branding to breakdown page
    let yPosition = await this.addLogoToPDF(doc, 15);

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("COST BREAKDOWN", 105, yPosition + 10, { align: "center" });
    
    yPosition += 25;

    const breakdownData = [
      ["Construction Work", `£${summary.constructionSubtotal.toFixed(2)}`],
      ["  - Labour", `£${summary.labourTotal.toFixed(2)}`],
      ["  - Material", `£${summary.materialTotal.toFixed(2)}`],
      ["  - Plant", `£${summary.plantTotal.toFixed(2)}`],
      ["", ""],
      ["Overhead (15%)", `£${summary.overhead.toFixed(2)}`],
      ["Profit (8%)", `£${summary.profit.toFixed(2)}`],
      ["", ""],
      ["GRAND TOTAL", `£${summary.grandTotal.toFixed(2)}`],
    ];

    doc.autoTable({
      startY: yPosition,
      body: breakdownData,
      styles: {
        fontSize: 12,
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 50, halign: "right", fontStyle: "bold" },
      },
      margin: { left: 30, right: 30 },
    });

    // Section breakdown
    const sectionData = [
      ["Foul Sewers", `£${summary.sections.foulSewers.toFixed(2)}`],
      ["Storm Sewers", `£${summary.sections.stormSewers.toFixed(2)}`],
      ["Roads", `£${summary.sections.roads.toFixed(2)}`],
      ["Preliminary Items", `£${summary.sections.preliminaryItems.toFixed(2)}`],
    ];

    const finalY = (doc as any).lastAutoTable.finalY + 20;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("SECTION BREAKDOWN", 30, finalY);

    doc.autoTable({
      startY: finalY + 10,
      body: sectionData,
      styles: {
        fontSize: 12,
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 50, halign: "right", fontStyle: "bold" },
      },
      margin: { left: 30, right: 30 },
    });
  }

  /**
   * Escape HTML-significant characters so untrusted free-text fields (project name,
   * client, item descriptions/status) can't inject markup into the print preview,
   * which is rendered via dangerouslySetInnerHTML and document.write.
   */
  private static escapeHtml(value: unknown): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /**
   * Generate print-friendly HTML for preview
   */
  static generatePrintHTML(
    sections: BoQSection[],
    summary: BoQSummary,
    tenderState: TenderState
  ): string {
    const safeProjectName = this.escapeHtml(tenderState.projectName);
    const safeClient = this.escapeHtml(tenderState.client);

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill of Quantities - ${safeProjectName}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .logo-container { display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
          .logo { max-height: 60px; margin-right: 20px; }
          .company-info { text-align: left; }
          .company-name { font-size: 18px; font-weight: bold; color: #1e3a8a; margin-bottom: 5px; }
          .tagline { font-size: 12px; color: #64748b; font-style: italic; }
          .title { font-size: 16px; font-weight: bold; margin-bottom: 20px; }
          .project-info { text-align: left; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed; }
          th, td { 
            border: 1px solid #000; 
            padding: 6px 8px; 
            text-align: left; 
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: break-word;
            hyphens: auto;
            line-height: 1.4;
          }
          th { background-color: #f0f0f0; font-weight: bold; }
          .amount { text-align: right; white-space: nowrap; }
          .header-row { font-weight: bold; background-color: #f8f8f8; }
          .subtotal-row { font-style: italic; background-color: #f0f0f0; }
          .total-row { font-weight: bold; background-color: #e0e0e0; }
          .section-header { font-weight: bold; font-size: 14px; background-color: #d0d0d0; }
          .empty-row { border: none; }
          .grand-total { font-size: 16px; font-weight: bold; text-align: center; margin-top: 20px; }
          .amount { text-align: right; }
          .yellow-highlight { background-color: #ffff00; }
          @media print { 
            body { margin: 0; } 
            .no-print { display: none; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="${mjLogoPath}" alt="M&J Evans Construction" class="logo" />
            <div class="company-info">
              <div class="company-name">M & J EVANS CONSTRUCTION LIMITED</div>
              <div class="tagline">The Groundworks Partner of Choice</div>
            </div>
          </div>
          <div class="title">BILL OF QUANTITIES</div>
        </div>
        <div class="project-info">
          <div>Project: ${safeProjectName}</div>
          <div>Client: ${safeClient}</div>
          <div>Date: ${new Date().toLocaleDateString()}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 70%; background-color: #ffff00;">Description</th>
              <th style="width: 30%; background-color: #ffff00;">Amount</th>
            </tr>
          </thead>
          <tbody>
    `;

    sections.forEach((section) => {
      section.items.forEach((item) => {
        let rowClass = "";
        let cellContent = "";
        let backgroundColor = "";

        // Yellow highlighting for savings items and capping layers
        if (item.isHighlighted || item.isSavingsItem) {
          backgroundColor = "background-color: #ffff00;";
        }

        if (item.description === "") {
          cellContent = `<tr class="empty-row"><td colspan="2">&nbsp;</td></tr>`;
        } else if (item.isHeader && item.level === 0) {
          rowClass = "section-header";
          cellContent = `<tr class="${rowClass}"><td colspan="2" style="font-weight: bold;">${this.escapeHtml(item.description)}</td></tr>`;
        } else {
          const amount = item.amount > 0 ? "£" + item.amount.toFixed(2) : "";
          const statusText = item.status
            ? `<br><em style="font-size: 10px; color: #666;">${this.escapeHtml(item.status)}</em>`
            : "";

          cellContent = `<tr style="${backgroundColor}">
            <td>${this.escapeHtml(item.description)}</td>
            <td class="amount">${amount}${statusText}</td>
          </tr>`;
        }

        html += cellContent;
      });
    });

    html += `
          </tbody>
        </table>
        
        <div class="grand-total">
          GRAND TOTAL: £${summary.grandTotal.toFixed(2)}
        </div>
      </body>
      </html>
    `;

    return html;
  }
}
